// react
import React from 'react';

// third-party
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

// application
import AsyncAction from '../shared/AsyncAction';
import { Cart16Svg } from '../../svg';
import { url } from '../../services/utils';
import { updateAction } from '../../store/auth/authActions';

export default function Suggestions(props) {
    const {
        context,
        className,
        products,
        isLoading,
        onItemClick,
        onAddToCartSuccess,
        query,
        wh_id,
    } = props;

    const { token } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const history = useHistory();

    const rootClasses = classNames(`suggestions suggestions--location--${context}`, className);
    const formatRupiah = (money) => {
        return new Intl.NumberFormat('id-ID',
            { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }
        ).format(money);
    };

    const getActiveToken = () => {
        if (token) return token;
        try {
            const stored = JSON.parse(localStorage.getItem('state') || '{}');
            const found = stored?.auth?.token || localStorage.getItem('token');
            if (found) {
                localStorage.setItem('token', found);
                return found;
            }
        } catch (e) {
            return localStorage.getItem('token');
        }
        return localStorage.getItem('token');
    };

    const handleAddToCart = async (product) => {
        const activeToken = getActiveToken();
        if (!activeToken) {
            toast.warning('Silakan login terlebih dahulu untuk menambah ke keranjang');
            history.push('/account/login');
            return;
        }

        const body = {
            item_id: product.item_id || product.id,
            qty: 1,
            type: product.type || 'normal',
            price_6: product.price_6 || product.price || 0,
            warehouse_id: product.warehouse_id || 1,
        };

        try {
            await axios({
                method: 'POST',
                url: `${process.env.REACT_APP_HOST_API}/cart/save`,
                headers: {
                    Authorization: `Bearer ${activeToken}`,
                },
                data: body,
            });

            toast.success('Produk berhasil ditambahkan ke keranjang');

            // Instantly sync realtime cart total
            try {
                const res = await axios({
                    method: 'GET',
                    url: `${process.env.REACT_APP_HOST_API}/cart/list`,
                    headers: {
                        Authorization: `Bearer ${activeToken}`,
                    },
                });
                const items = Array.isArray(res.data?.response) ? res.data.response : [];
                const sum = items.reduce((acc, curr) => acc + (parseInt(curr.qty, 10) || 0), 0);
                localStorage.setItem('cart_total', sum);
                window.dispatchEvent(new CustomEvent('cart:count_updated', { detail: sum }));
            } catch (err) {
                const current = parseInt(localStorage.getItem('cart_total'), 10) || 0;
                localStorage.setItem('cart_total', current + 1);
                window.dispatchEvent(new CustomEvent('cart:count_updated', { detail: current + 1 }));
            }

            // Dispatch cart updated event to notify Cart page in realtime
            window.dispatchEvent(new CustomEvent('cart:item_added', { detail: product }));
            window.dispatchEvent(new CustomEvent('cart:updated', { detail: product }));

            // Trigger global components refresh
            dispatch(updateAction(Date.now()));

            // Clear search input and close search recommendations popup
            if (onAddToCartSuccess) {
                onAddToCartSuccess();
            } else if (onItemClick) {
                onItemClick();
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            const msg = error.response?.data?.message || 'Gagal menambahkan produk ke keranjang';
            toast.error(msg);
        }
    };

    if (isLoading) {
        return (
            <div className={rootClasses}>
                <div className="suggestions__loading">
                    <div className="suggestions__loading-spinner" />
                    <span>Mencari produk...</span>
                </div>
            </div>
        );
    }

    const isEmpty = products === null || (Array.isArray(products) && products.length === 0);
    const list = isEmpty ? (
        <li className="suggestions__item suggestions__item--empty">
            Data yang Anda cari tidak ada
        </li>
    ) : (
        products.map((product) => (
            <li key={product.id} className="suggestions__item">
                {product.image_url && product.image_url.length > 0 && (
                    <div className="suggestions__item-image product-image">
                        <div className="product-image__body">
                            <Link to={url.product(product)} onClick={onItemClick}>
                                <img className="product-image__img" src={product.image_url} alt="" />
                            </Link>
                        </div>
                    </div>
                )}
                <div className="suggestions__item-info">
                    <Link className="suggestions__item-name" to={url.product(product)} onClick={onItemClick}>
                        {product.item_name}
                    </Link>
                </div>
                <div className="suggestions__item-price">
                    {product.compareAtPrice && (
                        <React.Fragment>
                            <span className="suggestions__item-price-new">
                                <div className="product-card__prices">
                                    {formatRupiah(product.price_6)}
                                </div>
                            </span>
                            {' '}
                            <span className="suggestions__item-price-old">{formatRupiah(product.compareAtPrice)}</span>
                        </React.Fragment>
                    )}

                    {!product.compareAtPrice && formatRupiah(product.price_6)}
                </div>
                {(context === 'header' || context === 'mobile-header') && (
                    <div className="suggestions__item-actions">
                        <AsyncAction
                            action={() => handleAddToCart(product)}
                            render={({ run, loading }) => (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        run();
                                    }}
                                    disabled={loading}
                                    title="Tambah ke keranjang"
                                    className={classNames('btn btn-primary btn-sm btn-svg-icon', {
                                        'btn-loading': loading,
                                    })}
                                >
                                    {loading ? <i className="fas fa-spinner fa-spin" /> : <Cart16Svg />}
                                </button>
                            )}
                        />
                    </div>
                )}
            </li>
        ))
    );

    return (
        <div className={rootClasses}>
            <ul className="suggestions__list">
                {list}
            </ul>
            {!isEmpty && query && query.trim().length >= 3 && context === 'mobile-header' && (
                <div className="suggestions__footer">
                    <Link
                        to={`/shop/category?name=${encodeURIComponent(query.trim())}&wh_id=${wh_id || 3}`}
                        onClick={onItemClick}
                        className="suggestions__view-all"
                    >
                        <span>Lihat semua hasil untuk "{query.trim()}"</span>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </Link>
                </div>
            )}
        </div>
    );
}
